// Reusable bottom-pinned input bar. Mirrors the dashboard composer's placement
// and look (wide, glass, page visible behind) but is generic: each page passes
// its own placeholder + submit handler, so the same bar adds a routine on the
// Daily page, a task to the open group on the Team page, etc. — matching how
// the mobile app's single bottom composer adapts to the active tab.
//
// Optionally a page can pass an `optionsPanel` node: a "+" button then appears
// on the left of the bar and toggles that panel open above it (mirroring the
// mobile composer's "+" that reveals schedule / sharing options). The page owns
// the option state; this component only shows/hides the panel and closes it
// after a successful submit.

import React, { useState } from 'react';
import { ArrowUp, Plus, X } from 'lucide-react';
import VoiceInputButton from './VoiceInputButton';
import './FloatingComposer.css';

function FloatingComposer({ placeholder = 'Add…', onSubmit, disabled = false, hint, optionsPanel }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const hasOptions = !!optionsPanel;

  const submit = async () => {
    const text = value.trim();
    if (!text || disabled || busy) return;
    setBusy(true);
    try {
      await onSubmit(text);
      setValue('');
      setOptionsOpen(false);
    } catch (_) {
      /* caller surfaces errors */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="floating-composer">
      <div className="floating-composer-inner">
        {hint && <div className="floating-composer-hint">{hint}</div>}

        {hasOptions && optionsOpen && !disabled && (
          <div className="floating-composer-options">{optionsPanel}</div>
        )}

        <div className={`floating-composer-bar${disabled ? ' disabled' : ''}`}>
          {hasOptions && (
            <button
              type="button"
              className={`floating-composer-plus${optionsOpen ? ' open' : ''}`}
              onClick={() => setOptionsOpen((o) => !o)}
              disabled={disabled}
              aria-label={optionsOpen ? 'Hide options' : 'Show options'}
            >
              {optionsOpen ? <X size={18} /> : <Plus size={18} />}
            </button>
          )}

          <input
            className="floating-composer-input"
            value={value}
            placeholder={placeholder}
            disabled={disabled || busy}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          />
          {!disabled && (
            <VoiceInputButton onResult={(t) => setValue((prev) => (prev ? `${prev} ` : '') + t)} />
          )}
          <button
            className="floating-composer-send"
            onClick={submit}
            disabled={disabled || busy || !value.trim()}
            aria-label="Add"
          >
            <ArrowUp size={18} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default FloatingComposer;
